import { z } from "zod";

/// The /v1 reads Hand draws as cards. Lenient on purpose: the server strips null fields before
/// they reach the browser, so every nullable column is optional, and unknown keys are dropped
/// rather than rejected so an additive /v1 field never turns a card back into a plain line.

const insightUrgencySchema = z.enum(["low", "medium", "high", "critical"]);
export type InsightUrgency = z.infer<typeof insightUrgencySchema>;

export const pulseScoresDataSchema = z.object({
  cells: z.array(
    z.object({
      dimension: z.string(),
      label: z.string(),
      score: z.number().optional(),
      insufficientData: z.boolean().optional(),
    }),
  ),
});
export type PulseScoreCell = z.infer<typeof pulseScoresDataSchema>["cells"][number];

export const pulseInsightsDataSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      insightType: z.string(),
      urgency: insightUrgencySchema.optional().catch(undefined),
      untrustedSummary: z.string().optional(),
    }),
  ),
  nextCursor: z.string().optional(),
});
export type PulseInsightItem = z.infer<typeof pulseInsightsDataSchema>["items"][number];

export const pulseActionsDataSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      untrustedTitle: z.string(),
      status: z.string(),
      owner: z.object({ name: z.string() }).optional(),
      expectedCompletionDate: z.string().optional(),
      isBlocked: z.boolean().optional(),
    }),
  ),
  nextCursor: z.string().optional(),
});
export type PulseActionItem = z.infer<typeof pulseActionsDataSchema>["items"][number];

export const peopleDataSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      title: z.string().optional(),
      department: z.string().optional(),
      status: z.string().optional(),
    }),
  ),
  nextCursor: z.string().optional(),
});
export type PersonItem = z.infer<typeof peopleDataSchema>["items"][number];

export const leaveBalancesDataSchema = z.object({
  balances: z.array(
    z.object({
      leaveType: z.object({ id: z.string(), name: z.string() }),
      available: z.string(),
      taken: z.string().optional(),
      credited: z.string().optional(),
      isActive: z.boolean().optional(),
    }),
  ),
});
export type LeaveBalanceItem = z.infer<typeof leaveBalancesDataSchema>["balances"][number];

export const todosDataSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      status: z.string(),
      dueDate: z.string().optional(),
      priority: z.string().optional(),
      assignees: z.array(z.object({ name: z.string() })).optional(),
    }),
  ),
  nextCursor: z.string().optional(),
});
export type TodoItem = z.infer<typeof todosDataSchema>["items"][number];

export const goalsDataSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      untrustedTitle: z.string(),
      progress: z.number().optional(),
      health: z.string().optional(),
      status: z.string().optional(),
      dueDate: z.string().optional(),
    }),
  ),
  nextCursor: z.string().optional(),
});
export type GoalItem = z.infer<typeof goalsDataSchema>["items"][number];

export const absenteesDataSchema = z.object({
  date: z.string().optional(),
  items: z.array(
    z.object({
      person: z.object({ id: z.string(), name: z.string() }),
      fromDate: z.string(),
      toDate: z.string(),
    }),
  ),
  nextCursor: z.string().optional(),
});
export type AbsenteeItem = z.infer<typeof absenteesDataSchema>["items"][number];

export const pendingSurveysDataSchema = z.object({
  items: z.array(
    z.object({
      deliveryId: z.string(),
      untrustedTitle: z.string(),
      dueAt: z.string().optional(),
      answeredCount: z.number().optional(),
      questionCount: z.number().optional(),
    }),
  ),
  nextCursor: z.string().optional(),
});
export type PendingSurveyItem = z.infer<typeof pendingSurveysDataSchema>["items"][number];

/// Both setup checklists (`leave.setupStatus.get`, `attendance.setupStatus.get`) share one shape.
/// Step keys stay plain strings so a step the server adds later still renders as a row, just
/// without a link; states are an enum because the row's icon and word depend on them.
export const setupStatusDataSchema = z.object({
  overall: z.enum(["ready", "review", "needs_setup"]),
  steps: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      state: z.enum(["ready", "review", "needs_setup", "optional"]),
      detail: z.string(),
    }),
  ),
});
export type SetupStatusData = z.infer<typeof setupStatusDataSchema>;
export type SetupStatusStep = SetupStatusData["steps"][number];
